import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Plus, Trash2, Save, ClipboardList } from "lucide-react";

/**
 * La scheda "Compila POS": tutto quello che il documento chiede e che l'app
 * non puo' sapere da sola.
 *
 * Le sezioni non sono scritte qui: arrivano dal server, con le voci e le
 * misure gia' proposte. Cosi' quando il modello cambia si tocca un posto solo,
 * e questa pagina si adegua senza essere riscritta.
 */
export default function CompilaPos({ site, onSalvato }) {
  const [sezioni, setSezioni] = useState([]);
  const [dati, setDati] = useState({});
  const [aggiornata, setAggiornata] = useState(null);
  const [carico, setCarico] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sporco, setSporco] = useState(false);

  const carica = useCallback(async () => {
    setCarico(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/pos/sezioni"),
        api.get(`/sites/${site.id}/pos/compilazione`),
      ]);
      setSezioni(s.data);
      setDati(c.data.sezioni || {});
      setAggiornata(c.data.aggiornata_il);
      setSporco(false);
    } catch { toast.error("Errore nel caricamento"); }
    finally { setCarico(false); }
  }, [site.id]);

  useEffect(() => { carica(); }, [carica]);

  const scrivi = (codice, valore) => {
    setDati((p) => ({ ...p, [codice]: valore }));
    setSporco(true);
  };

  const salva = async () => {
    setBusy(true);
    try {
      await api.put(`/sites/${site.id}/pos/compilazione`, { sezioni: dati });
      toast.success("Compilazione salvata");
      setSporco(false);
      onSalvato?.();
      carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally { setBusy(false); }
  };

  if (carico) return <p className="text-sm text-slate-500 py-6">Caricamento...</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap sticky top-0 bg-white/95 backdrop-blur py-2 z-10 border-b border-slate-100">
        <ClipboardList className="w-4 h-4 text-slate-400" />
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-semibold">Compila POS</div>
          <div className="text-xs text-slate-500">
            {aggiornata
              ? `Ultimo salvataggio ${new Date(aggiornata).toLocaleString("it-IT")}`
              : "Mai compilato"}
            {sporco && <span className="text-orange-600"> · modifiche non salvate</span>}
          </div>
        </div>
        <Button onClick={salva} disabled={busy || !sporco}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="salva-compilazione">
          <Save className="w-4 h-4 mr-2" />{busy ? "Salvo..." : "Salva"}
        </Button>
      </div>

      {sezioni.map((s) => (
        <Card key={s.codice} className="border-slate-200">
          <CardContent className="p-4 space-y-3">
            <div>
              <div className="text-sm font-semibold">
                <span className="text-slate-400 mr-2">{s.numero}</span>{s.titolo}
              </div>
              {s.nota && <p className="text-xs text-slate-500 mt-0.5">{s.nota}</p>}
            </div>
            <Sezione sezione={s} valore={dati[s.codice]} site={site}
                     onCambia={(v) => scrivi(s.codice, v)} />
          </CardContent>
        </Card>
      ))}

      <div className="flex justify-end">
        <Button onClick={salva} disabled={busy || !sporco}
                className="bg-orange-500 hover:bg-orange-600 text-white">
          <Save className="w-4 h-4 mr-2" />{busy ? "Salvo..." : "Salva la compilazione"}
        </Button>
      </div>
    </div>
  );
}

function Sezione({ sezione, valore, site, onCambia }) {
  switch (sezione.tipo) {
    case "righe":
      return <Righe sezione={sezione} valore={valore} site={site} onCambia={onCambia} />;
    case "spunte": return <Spunte sezione={sezione} valore={valore} onCambia={onCambia} />;
    case "spunte_misura":
      return <SpunteMisura sezione={sezione} valore={valore} onCambia={onCambia} />;
    case "spunte_dettaglio":
      return <SpunteDettaglio sezione={sezione} valore={valore} onCambia={onCambia} />;
    case "campi": return <Campi sezione={sezione} valore={valore} onCambia={onCambia} />;
    case "persone":
      return <Persone sezione={sezione} valore={valore} site={site} onCambia={onCambia} />;
    case "schede":
      return <Schede sezione={sezione} valore={valore} onCambia={onCambia} />;
    default: return null;
  }
}

function Schede({ sezione, valore, onCambia }) {
  const scelte = valore || [];
  const [schede, setSchede] = useState([]);
  const [cerca, setCerca] = useState("");

  useEffect(() => {
    api.get("/pos/schede?solo_attive=true")
      .then(({ data }) => setSchede(data))
      .catch(() => setSchede([]));
  }, []);

  const alterna = (id) =>
    onCambia(scelte.includes(id) ? scelte.filter((x) => x !== id) : [...scelte, id]);

  const visibili = cerca.trim()
    ? schede.filter((s) => (s.titolo + " " + s.codice).toLowerCase()
                             .includes(cerca.trim().toLowerCase()))
    : schede;
  const perFamiglia = visibili.reduce((acc, s) => {
    (acc[s.famiglia] = acc[s.famiglia] || []).push(s);
    return acc;
  }, {});

  if (schede.length === 0) {
    return (
      <p className="text-xs text-slate-500 border border-dashed border-slate-300 rounded p-3">
        Il catalogo delle schede è vuoto. Si carica da Cantieri, pulsante Schede POS.
      </p>
    );
  }

  // Da codice stampato sulla scheda («SCAM 1») all'id in archivio: i gruppi
  // sono scritti per codice, perche' il codice e' quello che si legge sulla
  // scheda e non cambia da un'installazione all'altra.
  const idPerCodice = {};
  for (const s of schede) idPerCodice[s.codice] = s.id;

  const gruppi = (sezione?.gruppi || []).map((g) => {
    const dentro = g.schede.map((c) => idPerCodice[c]).filter(Boolean);
    const mancanti = g.schede.filter((c) => !idPerCodice[c]);
    return {
      ...g, ids: dentro, mancanti,
      acceso: dentro.length > 0 && dentro.every((id) => scelte.includes(id)),
    };
  });

  /** Acceso: le sue schede si aggiungono. Spento: si tolgono, ma restano
   *  quelle che servono a un altro gruppo ancora acceso. */
  const alternaGruppo = (g) => {
    if (!g.acceso) {
      onCambia([...new Set([...scelte, ...g.ids])]);
      return;
    }
    const tenute = new Set(
      gruppi.filter((x) => x.codice !== g.codice && x.acceso)
            .flatMap((x) => x.ids));
    onCambia(scelte.filter((id) => !g.ids.includes(id) || tenute.has(id)));
  };

  return (
    <div className="space-y-2">
      {gruppi.length > 0 && (
        <div className="border border-slate-200 rounded p-2 bg-slate-50/60">
          <div className="text-[11px] text-slate-500 mb-1.5">
            Che lavoro è? Tocca uno o più tipi: le schede si spuntano da sole,
            e si sommano.
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {gruppi.map((g) => (
              <button key={g.codice} type="button" onClick={() => alternaGruppo(g)}
                      data-testid={`gruppo-${g.codice}`}
                      title={g.descrizione + (g.mancanti.length
                        ? ` · non in catalogo: ${g.mancanti.join(", ")}` : "")}
                      className={`h-8 px-3 rounded-full border text-xs font-medium transition
                        ${g.acceso
                          ? "bg-[#0067b2] text-white border-[#0067b2]"
                          : "bg-white text-slate-600 border-slate-300 hover:border-[#0067b2]"}`}>
                {g.nome}
                <span className={`ml-1.5 ${g.acceso ? "text-white/70" : "text-slate-400"}`}>
                  {g.ids.length}
                </span>
              </button>
            ))}
          </div>
          {gruppi.some((g) => g.mancanti.length > 0) && (
            <p className="text-[11px] text-amber-700 mt-1.5">
              Alcune schede dei gruppi non sono in catalogo e non si possono
              spuntare: {[...new Set(gruppi.flatMap((g) => g.mancanti))].join(", ")}.
            </p>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-500">{scelte.length} spuntate</span>
        {scelte.length > 0 && (
          <button type="button" className="text-xs text-slate-400 hover:text-red-600"
                  onClick={() => onCambia([])}>
            togli tutte
          </button>
        )}
        <input value={cerca} onChange={(e) => setCerca(e.target.value)}
               placeholder="Cerca..."
               className="ml-auto h-8 border border-slate-200 rounded-full px-3 text-xs w-44" />
      </div>
      <div className="border border-slate-200 rounded max-h-72 overflow-y-auto divide-y divide-slate-100">
        {Object.entries(perFamiglia).map(([fam, elenco]) => (
          <div key={fam}>
            <div className="px-2 py-1 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
              {fam}
            </div>
            {elenco.map((s) => (
              <label key={s.id}
                     className="px-2 py-1.5 flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={scelte.includes(s.id)}
                       onChange={() => alterna(s.id)} />
                <span className="font-semibold w-16 flex-shrink-0">{s.codice}</span>
                <span className="truncate" title={s.titolo}>{s.titolo}</span>
              </label>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Righe({ sezione, valore, site, onCambia }) {
  // La prima volta si trovano gia' scritte le fasi che ci sono su ogni
  // cantiere: si correggono invece di ribatterle. E le due date che il
  // cantiere sa gia' - inizio e fine prevista - arrivano da li'.
  const date = sezione.date_dal_cantiere;
  const righe = valore && valore.length
    ? valore
    : (sezione.predefinite || [{}]).map((r, i, tutte) => {
        const riga = { ...r };
        if (date && i === 0 && site?.start_date) {
          riga[date.prima] = String(site.start_date).substring(0, 10);
        }
        if (date && i === tutte.length - 1 && site?.end_date_planned) {
          riga[date.ultima] = String(site.end_date_planned).substring(0, 10);
        }
        return riga;
      });

  const cambia = (i, codice, v) =>
    onCambia(righe.map((r, k) => (k === i ? { ...r, [codice]: v } : r)));

  return (
    <div className="space-y-1.5">
      {righe.map((r, i) => (
        <div key={i} className="flex gap-1.5 items-center">
          {sezione.colonne.map((c) => (
            <Input key={c.codice} className="h-8 text-sm"
                   type={c.tipo === "data" ? "date" : "text"}
                   placeholder={c.nome} value={r[c.codice] || ""}
                   onChange={(e) => cambia(i, c.codice, e.target.value)} />
          ))}
          <button type="button" title="Togli la riga"
                  className="text-slate-300 hover:text-red-600 px-1 flex-shrink-0"
                  onClick={() => onCambia(righe.filter((_, k) => k !== i))}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm"
              onClick={() => onCambia([...righe, {}])}>
        <Plus className="w-3 h-3 mr-1" /> Aggiungi riga
      </Button>
    </div>
  );
}

function Spunte({ sezione, valore, onCambia }) {
  const v = valore || {};
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1">
        {sezione.voci.map((x) => (
          <label key={x.codice} className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={Boolean(v[x.codice])}
                   onChange={(e) => onCambia({ ...v, [x.codice]: e.target.checked })} />
            <span className="truncate" title={x.nome}>{x.nome}</span>
          </label>
        ))}
      </div>
      {sezione.altro && (
        <Input className="h-8 text-sm" placeholder="Altro, da scrivere"
               value={v.altro || ""}
               onChange={(e) => onCambia({ ...v, altro: e.target.value })} />
      )}
    </div>
  );
}

function SpunteMisura({ sezione, valore, onCambia }) {
  const v = valore || {};
  const alterna = (codice, scelto) =>
    onCambia({ ...v, [codice]: scelto ? { ...(v[codice] || {}), scelto: true } : undefined });

  return (
    <div className="space-y-2">
      {sezione.voci.map((x) => {
        const dato = v[x.codice];
        const scelto = Boolean(dato);
        return (
          <div key={x.codice}
               className={`border rounded p-2 ${scelto ? "border-[#0067b2]/30 bg-sky-50/40" : "border-slate-200"}`}>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={scelto}
                     onChange={(e) => alterna(x.codice, e.target.checked)} />
              <span>{x.nome}</span>
            </label>
            {scelto && (
              <textarea rows={2}
                        className="w-full mt-2 border border-slate-200 rounded p-2 text-xs"
                        value={dato.misure !== undefined ? dato.misure : (x.misura || "")}
                        onChange={(e) => onCambia({
                          ...v, [x.codice]: { ...dato, misure: e.target.value } })} />
            )}
          </div>
        );
      })}
      {sezione.altro && (
        <div className="border border-dashed border-slate-300 rounded p-2 space-y-1.5">
          <Input className="h-8 text-sm" placeholder="Altra situazione"
                 value={(v.altro || {}).nome || ""}
                 onChange={(e) => onCambia({
                   ...v, altro: { ...(v.altro || {}), nome: e.target.value } })} />
          <Input className="h-8 text-sm" placeholder="Misure previste"
                 value={(v.altro || {}).misure || ""}
                 onChange={(e) => onCambia({
                   ...v, altro: { ...(v.altro || {}), misure: e.target.value } })} />
        </div>
      )}
    </div>
  );
}

function SpunteDettaglio({ sezione, valore, onCambia }) {
  const v = valore || {};
  const colonne = sezione.colonne || ["Voce", "Descrizione", "Misure"];
  return (
    <div className="space-y-1.5">
      <div className="hidden md:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-slate-400 px-1">
        <div className="col-span-4">{colonne[0]}</div>
        <div className="col-span-3">{colonne[1]}</div>
        <div className="col-span-5">{colonne[2]}</div>
      </div>
      {sezione.voci.map((x) => {
        const dato = v[x.codice];
        const scelto = Boolean(dato);
        return (
          <div key={x.codice} className="grid grid-cols-12 gap-2 items-center">
            <label className="col-span-4 flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={scelto}
                     onChange={(e) => onCambia({
                       ...v,
                       [x.codice]: e.target.checked
                         ? { ...(dato || {}), scelto: true } : undefined })} />
              <span className="truncate" title={x.nome}>{x.nome}</span>
            </label>
            <Input className="col-span-3 h-8 text-xs" disabled={!scelto}
                   placeholder={x.descrizione || colonne[1]}
                   value={scelto && dato.descrizione !== undefined
                     ? dato.descrizione : (scelto ? (x.descrizione || "") : "")}
                   onChange={(e) => onCambia({
                     ...v, [x.codice]: { ...dato, descrizione: e.target.value } })} />
            <Input className="col-span-5 h-8 text-xs" disabled={!scelto}
                   placeholder={x.misura || colonne[2]}
                   value={scelto && dato.misure !== undefined
                     ? dato.misure : (scelto ? (x.misura || "") : "")}
                   onChange={(e) => onCambia({
                     ...v, [x.codice]: { ...dato, misure: e.target.value } })} />
          </div>
        );
      })}
      {sezione.altro && (
        <div className="grid grid-cols-12 gap-2 items-center pt-1">
          <Input className="col-span-4 h-8 text-xs" placeholder="Altro"
                 value={(v.altro || {}).nome || ""}
                 onChange={(e) => onCambia({
                   ...v, altro: { ...(v.altro || {}), nome: e.target.value } })} />
          <Input className="col-span-3 h-8 text-xs" placeholder={colonne[1]}
                 value={(v.altro || {}).descrizione || ""}
                 onChange={(e) => onCambia({
                   ...v, altro: { ...(v.altro || {}), descrizione: e.target.value } })} />
          <Input className="col-span-5 h-8 text-xs" placeholder={colonne[2]}
                 value={(v.altro || {}).misure || ""}
                 onChange={(e) => onCambia({
                   ...v, altro: { ...(v.altro || {}), misure: e.target.value } })} />
        </div>
      )}
    </div>
  );
}

function Campi({ sezione, valore, onCambia }) {
  const v = valore || {};
  const scrivi = (codice, x) => onCambia({ ...v, [codice]: x });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {sezione.campi.map((c) => (
        <div key={c.codice}>
          <Label className="text-xs">{c.nome}</Label>
          {c.tipo === "si_no" ? (
            <div className="flex gap-2 mt-1">
              {[["Sì", true], ["No", false]].map(([etichetta, quale]) => (
                <button key={etichetta} type="button"
                        onClick={() => scrivi(c.codice, quale)}
                        className={`flex-1 h-8 rounded border text-xs font-medium ${
                          v[c.codice] === quale
                            ? "bg-[#0067b2] text-white border-[#0067b2]"
                            : "bg-white text-slate-600 border-slate-200"}`}>
                  {etichetta}
                </button>
              ))}
            </div>
          ) : c.tipo === "scelta" ? (
            <select className="w-full h-8 border border-slate-200 rounded px-2 text-sm mt-1"
                    value={v[c.codice] || ""}
                    onChange={(e) => scrivi(c.codice, e.target.value)}>
              <option value="">—</option>
              {c.opzioni.map((o) => <option key={o}>{o}</option>)}
            </select>
          ) : (
            <Input className="h-8 text-sm mt-1"
                   type={c.tipo === "numero" ? "number" : "text"} step="0.1"
                   value={v[c.codice] === undefined || v[c.codice] === null
                     ? "" : v[c.codice]}
                   onChange={(e) => scrivi(c.codice,
                     c.tipo === "numero"
                       ? (e.target.value === "" ? "" : Number(e.target.value))
                       : e.target.value)} />
          )}
        </div>
      ))}
    </div>
  );
}

function Persone({ sezione, valore, site, onCambia }) {
  const v = valore || {};
  const [abilitati, setAbilitati] = useState(null);

  // Non si propone tutta la squadra: solo chi ha l'attestato valido. Chi non
  // ce l'ha non e' un addetto, e sul POS non ci deve finire.
  useEffect(() => {
    api.get(`/sites/${site.id}/pos/addetti`)
      .then(({ data }) => setAbilitati(data))
      .catch(() => setAbilitati({}));
  }, [site.id]);

  if (!abilitati) return <p className="text-xs text-slate-500">Caricamento...</p>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {sezione.voci.map((x) => {
        const info = abilitati[x.codice] || { persone: [], in_squadra: 0 };
        const nessuno = info.persone.length === 0;
        return (
          <div key={x.codice}>
            <Label className="text-xs">{x.nome}</Label>
            <select className="w-full h-9 border border-slate-200 rounded px-2 text-sm mt-1"
                    value={v[x.codice] || ""} disabled={nessuno}
                    onChange={(e) => onCambia({ ...v, [x.codice]: e.target.value })}>
              <option value="">Nessuno indicato</option>
              {info.persone.map((p) => (
                <option key={p.id} value={p.id}>{p.full_name}</option>
              ))}
            </select>
            <p className={`text-[10px] mt-1 ${nessuno ? "text-amber-700" : "text-slate-500"}`}>
              {nessuno
                ? `Nessuno in squadra ha l'attestato ${info.formazione} in corso di validità`
                : `${info.persone.length} su ${info.in_squadra} in squadra con attestato ${info.formazione}`}
            </p>
          </div>
        );
      })}
    </div>
  );
}
